import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Alert, Chip, Button,
  Skeleton, useTheme, alpha, Tooltip, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Paid as PaidIcon,
  Inventory2 as Inventory2Icon,
  Receipt as ReceiptIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  CategoryOutlined as CategoryIcon,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell,
  Tooltip as RechartsTooltip,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { useUnidade } from '../../contexts/UnidadeContext';
import { saudeService } from '../../services/api';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';

// ─── Tipos ───

interface Totais {
  qde_total: number;
  procedimentos_distintos: number;
  valor_total: number;
  valor_faturavel: number;
  pct_faturavel: number;
}

interface PorProcedimento {
  codprocedimento: string;
  nome: string;
  valor_unitario: number;
  qde: number;
  valor_total: number;
  faturavel: 'Sim' | 'Nao';
}

interface PorUnidade {
  codunidade: string;
  nome: string;
  qde: number;
  valor_total: number;
}

interface ProducaoAmbulatorial {
  totais: Totais;
  por_procedimento: PorProcedimento[];
  por_unidade: PorUnidade[];
  periodo: { inicio: string; fim: string };
  gerado_em: string;
}

const REFRESH_MS = 60_000;
const COR_QDE = '#1976d2';
const COR_VALOR = '#2e7d32';
const COR_FAT = '#00838f';
const COR_DISTINTOS = '#5e35b1';

const toISO = (d: Date): string => d.toISOString().slice(0, 10);
const num = (n?: number | null) => (n ?? 0).toLocaleString('pt-BR');
const moeda = (n?: number | null) => (n ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const pct = (n?: number | null) => `${(n ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

/** Periodo default: mes corrente (dia 1 -> hoje). */
const hoje = new Date();
const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);

// ─── KPI Card ───

const KpiCard: React.FC<{
  label: string; value: string; icon: React.ReactNode; color: string; loading: boolean;
}> = ({ label, value, icon, color, loading }) => {
  const theme = useTheme();
  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: 2, display: 'flex', alignItems: 'center',
          justifyContent: 'center', bgcolor: alpha(color, 0.12), color,
        }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          {loading
            ? <Skeleton width={88} height={36} />
            : <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{value}</Typography>}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{label}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── Pagina ───

const SaudeProducaoAmbulatorial: React.FC = () => {
  const theme = useTheme();
  const { municipioAtivo } = useAuth();
  const { unidadeSelecionada } = useUnidade();

  const [data, setData] = useState<ProducaoAmbulatorial | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<Date | null>(null);
  const [segundos, setSegundos] = useState(REFRESH_MS / 1000);

  // Filtros — periodo default: mes corrente
  const [dataInicio, setDataInicio] = useState(toISO(inicioMes));
  const [dataFim, setDataFim] = useState(toISO(hoje));

  const fetchRef = useRef<() => void>(() => {});

  const fetchData = useCallback(async (silencioso = false) => {
    if (!municipioAtivo?.id) return;
    if (!silencioso) setLoading(true);
    setError(null);
    try {
      const result = await saudeService.getProducaoAmbulatorial(municipioAtivo.id, {
        unidade: unidadeSelecionada?.codunidade,
        inicio: dataInicio,
        fim: dataFim,
      });
      setData(result);
      setUltimaAtualizacao(new Date());
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar a producao do periodo.');
    } finally {
      setLoading(false);
      setSegundos(REFRESH_MS / 1000);
    }
  }, [municipioAtivo?.id, unidadeSelecionada?.codunidade, dataInicio, dataFim]);

  useEffect(() => { fetchRef.current = () => fetchData(true); });

  // Busca inicial / ao trocar filtros
  useEffect(() => { fetchData(false); }, [fetchData]);

  // Auto-refresh a cada 60s (silencioso) + contador regressivo
  useEffect(() => {
    if (!municipioAtivo?.id) return;
    const refresh = setInterval(() => fetchRef.current(), REFRESH_MS);
    const tick = setInterval(() => setSegundos((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => { clearInterval(refresh); clearInterval(tick); };
  }, [municipioAtivo?.id]);

  const handleDateChange = useCallback((di: string, df: string) => {
    setDataInicio(di);
    setDataFim(df);
  }, []);

  const totais = data?.totais;
  const porProcedimento = data?.por_procedimento || [];
  const porUnidade = data?.por_unidade || [];
  const mostrarUnidades = !unidadeSelecionada && porUnidade.length > 1;

  // Top 12 procedimentos por quantidade (para o grafico)
  const topQuantidade = useMemo(
    () => [...porProcedimento].sort((a, b) => b.qde - a.qde).slice(0, 12),
    [porProcedimento],
  );

  const ProcedimentoTooltip = useMemo(() => ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload as PorProcedimento;
    return (
      <Box sx={{ bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 1, maxWidth: 280 }}>
        <Typography variant="caption" fontWeight={600} display="block">{d.codprocedimento} — {d.nome}</Typography>
        <Typography variant="caption" display="block" sx={{ color: COR_QDE }}>Quantidade: {num(d.qde)}</Typography>
        <Typography variant="caption" display="block" sx={{ color: COR_VALOR }}>Valor total: {moeda(d.valor_total)}</Typography>
      </Box>
    );
  }, [theme.palette.divider]);

  const UnidadeTooltip = useMemo(() => ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload as PorUnidade;
    return (
      <Box sx={{ bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 1 }}>
        <Typography variant="caption" fontWeight={600} display="block">{d.nome}</Typography>
        <Typography variant="caption" display="block" sx={{ color: COR_QDE }}>Quantidade: {num(d.qde)}</Typography>
        <Typography variant="caption" display="block" sx={{ color: COR_VALOR }}>Valor total: {moeda(d.valor_total)}</Typography>
      </Box>
    );
  }, [theme.palette.divider]);

  if (!municipioAtivo?.id) {
    return <Box sx={{ p: 3 }}><Alert severity="info">Selecione um municipio para visualizar a producao.</Alert></Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Cabecalho */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaidIcon color="primary" /> Produção Ambulatorial
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {unidadeSelecionada
              ? <>Unidade: <strong>{unidadeSelecionada.nome}</strong></>
              : 'Todas as unidades do municipio'}
            {' • Procedimentos, quantidade e valores no periodo'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {ultimaAtualizacao ? `Atualizado as ${ultimaAtualizacao.toLocaleTimeString('pt-BR')}` : 'Carregando...'}
            </Typography>
            <Typography variant="caption" color="text.disabled">Proxima atualizacao em {segundos}s</Typography>
          </Box>
          <Tooltip title="Atualizar agora">
            <span>
              <IconButton onClick={() => fetchData(false)} disabled={loading} color="primary"
                sx={{ border: `1px solid ${theme.palette.divider}` }}>
                <RefreshIcon sx={{ animation: loading ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Filtro de periodo */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
        <CardContent sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          <DateRangeSelector dataInicio={dataInicio} dataFim={dataFim} onChange={handleDateChange} />
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => fetchData(false)}>Tentar novamente</Button>}>{error}</Alert>}

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}><KpiCard label="Procedimentos (quantidade)" value={num(totais?.qde_total)} icon={<Inventory2Icon />} color={COR_QDE} loading={loading} /></Grid>
        <Grid item xs={6} md={3}><KpiCard label="Procedimentos distintos" value={num(totais?.procedimentos_distintos)} icon={<CategoryIcon />} color={COR_DISTINTOS} loading={loading} /></Grid>
        <Grid item xs={6} md={3}><KpiCard label="Valor total" value={moeda(totais?.valor_total)} icon={<AccountBalanceWalletIcon />} color={COR_VALOR} loading={loading} /></Grid>
        <Grid item xs={6} md={3}><KpiCard label={`Valor faturavel (${pct(totais?.pct_faturavel)})`} value={moeda(totais?.valor_faturavel)} icon={<ReceiptIcon />} color={COR_FAT} loading={loading} /></Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Top procedimentos por quantidade */}
        <Grid item xs={12} lg={mostrarUnidades ? 7 : 12}>
          <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Principais procedimentos por quantidade</Typography>
              {loading ? <Skeleton variant="rectangular" height={360} /> : (topQuantidade.length ? (
                <ResponsiveContainer width="100%" height={Math.max(360, topQuantidade.length * 34)}>
                  <BarChart data={topQuantidade} layout="vertical" margin={{ top: 4, right: 32, bottom: 4, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={alpha(theme.palette.text.primary, 0.08)} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="codprocedimento" width={88} tick={{ fontSize: 10 }} interval={0} />
                    <RechartsTooltip content={<ProcedimentoTooltip />} />
                    <Bar dataKey="qde" name="Quantidade" fill={COR_QDE} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <Box sx={{ py: 6, textAlign: 'center' }}><Typography color="text.secondary">Sem producao no periodo selecionado.</Typography></Box>)}
            </CardContent>
          </Card>
        </Grid>

        {/* Producao por unidade (somente com todas as unidades) */}
        {mostrarUnidades && (
          <Grid item xs={12} lg={5}>
            <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Valor por unidade</Typography>
                {loading ? <Skeleton variant="rectangular" height={360} /> : (
                  <ResponsiveContainer width="100%" height={Math.max(360, porUnidade.length * 40)}>
                    <BarChart data={porUnidade} layout="vertical" margin={{ top: 4, right: 32, bottom: 4, left: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={alpha(theme.palette.text.primary, 0.08)} />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="nome" width={150} tick={{ fontSize: 10 }} interval={0} />
                      <RechartsTooltip content={<UnidadeTooltip />} />
                      <Bar dataKey="valor_total" name="Valor total" radius={[0, 4, 4, 0]}>
                        {porUnidade.map((_, i) => <Cell key={i} fill={COR_VALOR} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Tabela detalhada de procedimentos */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Procedimentos, quantidade e valores
          </Typography>
          {loading ? <Skeleton variant="rectangular" height={360} /> : (porProcedimento.length ? (
            <TableContainer sx={{ maxHeight: 480 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Codigo</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Procedimento</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Quantidade</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Valor unitario</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Valor total</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>Faturavel</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {porProcedimento.map((p) => (
                    <TableRow key={p.codprocedimento} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{p.codprocedimento}</TableCell>
                      <TableCell sx={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Tooltip title={p.nome}><span>{p.nome}</span></Tooltip>
                      </TableCell>
                      <TableCell align="right">{num(p.qde)}</TableCell>
                      <TableCell align="right">{moeda(p.valor_unitario)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{moeda(p.valor_total)}</TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={p.faturavel === 'Sim' ? 'Sim' : 'Nao'}
                          color={p.faturavel === 'Sim' ? 'success' : 'default'}
                          variant={p.faturavel === 'Sim' ? 'filled' : 'outlined'} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : <Box sx={{ py: 6, textAlign: 'center' }}><Typography color="text.secondary">Sem producao no periodo selecionado.</Typography></Box>)}
        </CardContent>
      </Card>

      <Chip size="small" variant="outlined" sx={{ mt: 2 }}
        label={`Periodo: ${data?.periodo?.inicio ?? dataInicio} a ${data?.periodo?.fim ?? dataFim} • Valores conforme tabela de procedimentos (valor ambulatorial) • Faturavel = procedimento valido para faturamento no periodo`} />
    </Box>
  );
};

export default SaudeProducaoAmbulatorial;
